import { Dropdown } from 'primereact/dropdown'
import { FC, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { themeState } from '../../../utils/state-atoms'
import SvgXYChart from '../plot/SvgXYChart'
import { SectionEmpty } from './Section'
import {
  CHART_METRICS,
  ChartMetricKey,
  HeadlineRecord,
  pickAttitudeField,
  toChartPoints
} from './headlineRecords'

export type CoefficientChartsProps = {
  /** Headline records for the proposed run's cases. */
  records: HeadlineRecord[]
}

/** Coefficient-delta vs. attitude chart (Correlation report "Charts" section). */
const CoefficientCharts: FC<CoefficientChartsProps> = ({ records }) => {
  const theme = useRecoilValue(themeState)
  const [metric, setMetric] = useState<ChartMetricKey>('delta_cd')

  const xField = useMemo(() => pickAttitudeField(records), [records])
  const points = useMemo(() => toChartPoints(records, metric, xField), [records, metric, xField])

  if (!records.length) {
    return <SectionEmpty message="No coefficient data for the selected run's cases." />
  }

  const metricLabel = CHART_METRICS.find(m => m.key === metric)?.label ?? metric

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-gray-600 dark:text-gray-300">Metric</span>
        <Dropdown
          value={metric}
          options={CHART_METRICS.map(m => ({ label: m.label, value: m.key }))}
          onChange={e => setMetric(e.value)}
          className="w-40"
        />
      </div>
      <div className="h-[360px] w-full">
        {points.length ? (
          <SvgXYChart
            scatterPoints={points}
            xLabel={xField ? xField.toUpperCase() : 'Case'}
            yLabel={metricLabel}
            darkMode={theme === 'dark'}
            className="w-full h-full"
          />
        ) : (
          <SectionEmpty message="No values for this metric on the selected run." />
        )}
      </div>
    </div>
  )
}

export default CoefficientCharts
